import React, { useEffect, useRef } from 'react';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { useDispatch } from 'react-redux';

/**
 * LocationChangeDispatcher
 *
 * Dispatches @@router/LOCATION_CHANGE Redux actions on every TanStack Router
 * navigation. This preserves backward compatibility with the 17 reducers that
 * use connected-react-router's LOCATION_CHANGE action to reset stale state.
 *
 * Place this component inside BOTH <RouterProvider> and Redux <Provider>:
 *   <Provider store={store}>
 *     <RouterProvider router={router} />
 *   </Provider>
 *
 * The cleanest place is __root.tsx of any app that needs it (ppms-ui).
 */
export const LocationChangeDispatcher: React.FC = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const routerState = useRouterState();
  const isFirst = useRef(true);
  // router.history.subscribe fires synchronously before router state propagates
  // to React, so this ref always holds the correct action when the effect below runs.
  const lastAction = useRef<string>('POP');

  useEffect(() => router.history.subscribe(({ action }) => {
    lastAction.current = action.type;
  }), [router.history]);

  useEffect(() => {
    dispatch({
      type: '@@router/LOCATION_CHANGE',
      payload: {
        location: {
          pathname: routerState.location.pathname,
          search: routerState.location.searchStr ?? '',
          hash: routerState.location.hash ?? '',
          state: routerState.location.state,
          key: routerState.location.state?.key,
        },
        action: lastAction.current,
        isFirstRendering: isFirst.current,
      },
    });
    isFirst.current = false;
  }, [routerState.location.pathname, routerState.location.searchStr, routerState.location.hash]);

  return null;
};
